/**
 * User Management API
 *
 * Users are stored in the existing KV store via dedicated auth endpoints.
 * Each user record is stored under:
 *   co:__users:{username}  ->  UserRecord object
 *
 * SECURITY:
 *   - Passwords are hashed with bcrypt on the server
 *   - Login validation happens server-side (passwords never sent to client)
 *   - Session tokens are used for authenticated requests
 *   - The /users endpoint strips passwords from responses
 *   - Admin bootstrap is server-side only (no hardcoded passwords in client)
 *
 * Roles:
 *   - admin:          Full access — dashboard, all templates, edit content, manage users
 *   - characterizer:  All permissions except deleting masters + no user management
 *   - designer:       Full template visibility (like characterizer), can edit content
 *   - team_lead:      Full template visibility (like characterizer), can edit content
 *   - content_writer: Can edit content in assigned templates only
 *   - developer:      Can edit content in assigned templates only
 *   - marketer:       Can edit content in assigned templates only
 *   - client_manager: Can manage client relationships and access specific templates
 *   - onboarding:     Access to onboarding templates and content
 *
 * IMPROVEMENT #7: API_BASE and fetch helpers are now imported from the shared
 * apiClient module instead of being duplicated locally. This ensures that
 * retry logic, error logging, and 401 handling are consistent across the app.
 * The only exception is validateLogin() which uses raw fetch for the login
 * endpoint (no session token needed, no retry on auth failure).
 */

import { API_BASE, apiFetch, authFetch as sharedAuthFetch } from "./apiClient";
import { publicAnonKey } from "../../../utils/supabase/info";

export type UserRole = "admin" | "characterizer" | "designer" | "team_lead" | "content_writer" | "developer" | "marketer" | "client_manager" | "onboarding";

/** Roles that have full template visibility (see all templates) */
export const FULL_ACCESS_ROLES: UserRole[] = ["admin", "characterizer", "designer", "onboarding"];

/** Roles that can only see templates assigned to them */
export const RESTRICTED_ROLES: UserRole[] = ["team_lead", "content_writer", "developer", "marketer", "client_manager"];

export interface UserRecord {
  username: string;
  displayName: string;
  /** Password is no longer returned from server — only used when creating/updating */
  password?: string;
  /** Email address for receiving notification emails (optional) */
  email?: string;
  role: UserRole;
  /** Optional additional roles (e.g. admin who is also a characterizer) */
  additionalRoles?: UserRole[];
  /** "all" = access to every template, or array of specific template IDs */
  templateAccess: "all" | string[];
  createdAt: number;
  updatedAt: number;
}

/* ================================================================
   Session Token Management
   ================================================================ */

const SESSION_TOKEN_KEY = "zap_session_token";

/** Get the current session token */
export function getSessionToken(): string | null {
  try {
    return sessionStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Store a session token */
export function setSessionToken(token: string): void {
  try {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
  } catch {
    // ignore storage errors
  }
}

/** Clear the session token */
export function clearSessionToken(): void {
  try {
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    // ignore
  }
}

// --- Key helpers ---
const USER_PREFIX = "__users:";

function userKey(username: string): string {
  return USER_PREFIX + username.toLowerCase();
}

/* ================================================================
   In-memory cache for user list (avoids repeated server calls)
   ================================================================ */
let _usersCache: UserRecord[] | null = null;
let _usersCacheTime = 0;
const USERS_CACHE_TTL = 30_000; // 30 seconds

/** Invalidate the users cache (call after mutations) */
export function invalidateUsersCache(): void {
  _usersCache = null;
  _usersCacheTime = 0;
}

// --- Public API ---

/** List all users. Uses dedicated /users endpoint (fast). Admin bootstrap is server-side. */
export async function listUsers(): Promise<UserRecord[]> {
  // Return cached data if fresh
  if (_usersCache && (Date.now() - _usersCacheTime) < USERS_CACHE_TTL) {
    return _usersCache;
  }

  // ── Consume module-level prefetch (App.tsx) if available ──
  const prefetch = (window as any).__ZAP_PREFETCH_USERS;
  if (prefetch) {
    delete (window as any).__ZAP_PREFETCH_USERS; // consume once
    try {
      const prefetchData = await prefetch;
      if (prefetchData && prefetchData.content) {
        const raw: Record<string, any> = prefetchData.content;
        const users: UserRecord[] = [];
        for (const [key, value] of Object.entries(raw)) {
          if (key.startsWith(USER_PREFIX) && value && typeof value === "object" && (value as any).username) {
            users.push(value as UserRecord);
          }
        }
        const sorted = users.sort((a, b) => a.username.localeCompare(b.username));
        _usersCache = sorted;
        _usersCacheTime = Date.now();
        console.log("[userApi] Users loaded from prefetch");
        return sorted;
      }
    } catch {
      console.warn("[userApi] Prefetch failed, falling back to normal fetch");
    }
  }

  const data = await apiFetch("/users");
  const raw: Record<string, any> = data.content || {};
  const users: UserRecord[] = [];

  for (const [key, value] of Object.entries(raw)) {
    if (key.startsWith(USER_PREFIX) && value && typeof value === "object" && (value as any).username) {
      users.push(value as UserRecord);
    }
  }

  // NOTE: Admin bootstrap is handled server-side on cold start.
  // No client-side user creation needed.

  const sorted = users.sort((a, b) => a.username.localeCompare(b.username));
  _usersCache = sorted;
  _usersCacheTime = Date.now();
  return sorted;
}

/** 
 * Validate login credentials via secure server-side endpoint.
 * Returns user record (without password) and session token on success.
 *
 * NOTE: This function uses raw fetch (not apiFetch) because:
 *   1. No session token is needed for login
 *   2. We don't want retry on auth failures (wrong password)
 *   3. We want specific NETWORK_ERROR handling for the login UI
 */
export async function validateLogin(
  username: string,
  password: string,
): Promise<{ user: UserRecord; token: string } | null> {
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${publicAnonKey}`,
    };

    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers,
      body: JSON.stringify({ username, password }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      console.log("[userApi] Login failed:", data.error || res.status);
      return null;
    }

    const data = await res.json();
    if (data.success && data.user && data.token) {
      setSessionToken(data.token);
      invalidateUsersCache();
      return { user: data.user, token: data.token };
    }
    return null;
  } catch (err) {
    console.error("[userApi] Login network/fetch error:", err);
    throw new Error("NETWORK_ERROR");
  }
}

/** Validate the current session token. Returns fresh user data if valid.
 *
 * Throws a special NETWORK_ERROR when the server is unreachable (cold start,
 * offline, etc.) so callers can distinguish "server said invalid" from
 * "couldn't reach server".
 */
export async function validateSession(): Promise<UserRecord | null> {
  const token = getSessionToken();
  if (!token) return null;
  
  // Retry once after a short delay to handle edge function cold starts
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const data = await sharedAuthFetch("/auth/validate", {
        method: "POST",
        retry: { noRetry: true },
      });
      if (data.valid && data.user) {
        return data.user;
      }
      // Session invalid — server explicitly said so. Clear token.
      clearSessionToken();
      return null;
    } catch (err: any) {
      const msg = String(err?.message || "").toLowerCase();
      const isNetworkError = msg.includes("fetch") || msg.includes("network") || msg.includes("failed") || msg.includes("aborted");
      
      if (isNetworkError && attempt === 0) {
        // First attempt failed with network error — wait for cold start and retry
        await new Promise(r => setTimeout(r, 2500));
        continue;
      }
      
      if (isNetworkError) {
        console.warn("[validateSession] Network error after retry — not clearing session:", msg);
        throw new Error("NETWORK_ERROR");
      }
      // For explicit 401/403 responses parsed by apiFetch, the error message
      // contains "API error 4xx". That means the server IS reachable but the
      // session is invalid.
      clearSessionToken();
      return null;
    }
  }
  // Should never reach here, but just in case
  throw new Error("NETWORK_ERROR");
}

/** Create a new user (admin only — requires session token). */
export async function createUser(
  data: Omit<UserRecord, "createdAt" | "updatedAt"> & { password: string },
): Promise<UserRecord> {
  const result = await sharedAuthFetch("/auth/create-user", {
    method: "POST",
    body: JSON.stringify(data),
  });

  if (!result.success) {
    throw new Error(result.error || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D9\u05E6\u05D9\u05E8\u05EA \u05DE\u05E9\u05EA\u05DE\u05E9");
  }

  invalidateUsersCache();
  return result.user;
}

/** Update a user (admin can update anyone, user can update self). */
export async function updateUser(
  username: string,
  updates: Partial<Omit<UserRecord, "username" | "createdAt">>,
): Promise<UserRecord> {
  const result = await sharedAuthFetch("/auth/update-user", {
    method: "PUT",
    body: JSON.stringify({ username, ...updates }),
  });

  if (!result.success) {
    throw new Error(result.error || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E2\u05D3\u05DB\u05D5\u05DF \u05DE\u05E9\u05EA\u05DE\u05E9");
  }

  invalidateUsersCache();
  return result.user;
}

/** Change own password (requires current password verification). */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  const result = await sharedAuthFetch("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });

  if (!result.success) {
    throw new Error(result.error || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05D4\u05D7\u05DC\u05E4\u05EA \u05E1\u05D9\u05E1\u05DE\u05D0");
  }
}

/** Delete a user (admin only). Cannot delete the last admin. */
export async function deleteUser(username: string): Promise<void> {
  const result = await sharedAuthFetch("/auth/delete-user", {
    method: "DELETE",
    body: JSON.stringify({ username }),
  });

  if (!result.success) {
    throw new Error(result.error || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05DE\u05D7\u05D9\u05E7\u05EA \u05DE\u05E9\u05EA\u05DE\u05E9");
  }

  invalidateUsersCache();
}

/** Logout — clear session on server and client */
export async function serverLogout(): Promise<void> {
  try {
    await sharedAuthFetch("/auth/logout", {
      method: "POST",
      retry: { noRetry: true },
    });
  } catch {
    // Ignore — clear client-side anyway
  }
  clearSessionToken();
}

/** Get users filtered by role. */
export async function getUsersByRole(role: UserRole): Promise<UserRecord[]> {
  const users = await listUsers();
  return users.filter((u) => u.role === role);
}

/* ================================================================
   Multi-role helpers
   ================================================================ */

/** Get ALL roles a user has (primary + additional). */
export function getUserRoles(user: { role: UserRole; additionalRoles?: UserRole[] }): UserRole[] {
  const roles = new Set<UserRole>([user.role]);
  if (user.additionalRoles) {
    for (const r of user.additionalRoles) roles.add(r);
  }
  return Array.from(roles);
}

/** Check if a user has a specific role (primary or additional). */
export function userHasRole(user: { role: UserRole; additionalRoles?: UserRole[] }, role: UserRole): boolean {
  if (user.role === role) return true;
  return user.additionalRoles?.includes(role) ?? false;
}

/** Check if a user has any of the given roles. */
export function userHasAnyRole(user: { role: UserRole; additionalRoles?: UserRole[] }, roles: UserRole[]): boolean {
  return roles.some((r) => userHasRole(user, r));
}

/** Check if a user has full access (admin or characterizer as any role). */
export function userHasFullAccess(user: { role: UserRole; additionalRoles?: UserRole[] }): boolean {
  return userHasAnyRole(user, FULL_ACCESS_ROLES);
}

/** Filter users that have a specific role (primary or additional). */
export function filterUsersByRole(users: UserRecord[], role: UserRole): UserRecord[] {
  return users.filter((u) => userHasRole(u, role));
}

/* ================================================================
   Forgot Password API
   ================================================================ */

/** Request a password reset code to be sent via email */
export async function requestPasswordReset(username: string): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ username }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { success: false, error: data.error || `Server error: ${res.status}` };
    }
    return { success: true, message: data.message };
  } catch (err) {
    console.error("[userApi] requestPasswordReset error:", err);
    return { success: false, error: "NETWORK_ERROR" };
  }
}

/** Verify the 6-digit reset code */
export async function verifyResetCode(username: string, code: string): Promise<{ valid: boolean; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/verify-reset-code`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ username, code }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { valid: false, error: data.error || `Server error: ${res.status}` };
    }
    return { valid: data.valid, error: data.error };
  } catch (err) {
    console.error("[userApi] verifyResetCode error:", err);
    return { valid: false, error: "NETWORK_ERROR" };
  }
}

/** Reset password using a verified code. Returns session token on success. */
export async function resetPasswordWithCode(
  username: string,
  code: string,
  newPassword: string,
): Promise<{ success: boolean; user?: UserRecord; token?: string; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/auth/reset-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ username, code, newPassword }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { success: false, error: data.error || `Server error: ${res.status}` };
    }
    if (data.success && data.token) {
      setSessionToken(data.token);
      invalidateUsersCache();
      return { success: true, user: data.user, token: data.token };
    }
    return { success: false, error: "Unexpected response" };
  } catch (err) {
    console.error("[userApi] resetPasswordWithCode error:", err);
    return { success: false, error: "NETWORK_ERROR" };
  }
}

/* ================================================================
   Google OAuth Login API
   ================================================================ */

/** Login with Google ID token. Server verifies and matches to existing KV user by email. */
export async function googleLogin(idToken: string): Promise<{
  success: boolean;
  user?: UserRecord;
  token?: string;
  error?: string;
  errorCode?: string;
  email?: string;
}> {
  try {
    const res = await fetch(`${API_BASE}/auth/google-login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${publicAnonKey}`,
      },
      body: JSON.stringify({ idToken }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return {
        success: false,
        error: data.message || data.error || `Server error: ${res.status}`,
        errorCode: data.error,
        email: data.email,
      };
    }
    if (data.success && data.token) {
      setSessionToken(data.token);
      invalidateUsersCache();
      return { success: true, user: data.user, token: data.token };
    }
    return { success: false, error: "Unexpected response" };
  } catch (err) {
    console.error("[userApi] googleLogin error:", err);
    return { success: false, error: "NETWORK_ERROR" };
  }
}